<template>
  <div
    class="collection-item"
    ref="row-element"
    @contextmenu.exact.prevent="toggleShowMenu($event, true)">

    <!-- CONTEXT MENU -->
    <transition name="fade">
      <div
        v-if="contextMenu.displayFor.type === item.type && contextMenu.displayFor.id === item.id"
        class="collection-list-item-menu"
        :style="menuStyleObject"
        @click="$emit('hide-menu')"
        ref="contextMenu">
        <div v-if="!itemIsSelected">
          <div
            class="collection-list-item-menu-item"
            style="fontWeight: bold"
            @mousedown.prevent
            @click.exact="goToItem(item)"
            @click.ctrl.exact="addOpenItem(item)"
            @click.middle.exact.prevent="addOpenItem(item)">
            Open {{ item.type }}
          </div>
          <div
            class="collection-list-item-menu-item"
            v-if="item.item && item.item.type === 'file'"
            @mousedown.prevent
            @click.exact="$goToItem(item)"
            @click.ctrl.exact="openItemInBackground(item)"
            @click.middle.exact.prevent="openItemInBackground(item)">
            Open file
          </div>
          <div
            class="collection-list-item-menu-item"
            v-else-if="item.item && item.item.type === 'webpage'"
            @mousedown.prevent
            @click="$navigateTo({ externalUrl: item.item.address })"
            @click.middle.exact.prevent="$navigateTo({ externalUrl: item.item.address })">
            Open webpage
          </div>
          <div
            class="collection-list-item-menu-item disabled"
            v-else-if="item.type === 'card'">
            No item
          </div>
        </div>
        <div v-else>
          <div
            class="collection-list-item-menu-item collection-list-item-menu-header"
            style="fontWeight: bold">
            {{ selectCount }} items selected
          </div>
        </div>
        <div class="collection-list-item-menu-divider" />
        <div
          v-if="!itemIsSelected"
          class="collection-list-item-menu-item"
          @click="editItemTitle">
          Rename
        </div>
        <div
          class="collection-list-item-menu-item"
          @click="itemIsSelected ? $emit('move-selected') : $emit('move-item', { id: item.id, type: item.type })">
          Move
        </div>
        <div
          class="collection-list-item-menu-item"
          @mouseenter="contextMenuShowSections = true"
          @mouseleave="contextMenuShowSections = false">
          Section<i class="mdi mdi-chevron-right" style="float: right" />
          <transition name="fade">
            <div
              v-if="contextMenuShowSections"
              @mouseleave="contextMenuShowSections = false"
              class="collection-list-item-menu-sub"
              :style="subMenuStyleObject">
              <div
                class="collection-list-item-menu-item"
                style="fontStyle: italic"
                @click="itemIsSelected ? $emit('update-section', null) : updateSection(null)">
                None
                <i v-if="!item.section" class="mdi mdi-check" style="float: right" />
              </div>
              <div
                v-for="section in sections"
                :key="section"
                class="collection-list-item-menu-item"
                @click="itemIsSelected ? $emit('update-section', section) : updateSection(section)">
                {{ section }}
                <i v-if="item.section === section" class="mdi mdi-check" style="float: right" />
              </div>
              <div
                class="collection-list-item-menu-item"
                @click.stop>
                <input
                  class="new-section-input"
                  type="text"
                  placeholder="Add section"
                  v-model="newSection"
                  @keyup.enter="addSection" />
              </div>
            </div>
          </transition>
        </div>
        <div class="collection-list-item-menu-divider" />
        <div
          class="collection-list-item-menu-item"
          style="color: red"
          @click="deleteItem">
          Delete
        </div>
      </div>
    </transition>

    <!-- ITEM ROW -->
    <div
      class="collection-item-row"
      :class="{
        'collection-item-row-active': editTitle,
        'collection-item-row-selected': itemIsSelected || (contextMenu.displayFor.type === item.type && contextMenu.displayFor.id === item.id),
      }">
      <!-- SELECT / CONTEXT MENU -->
      <i
        class="mdi mdi-checkbox-blank-outline collection-list-select-icon"
        :class="{
          'display-select-checkboxes': batchSelectActive,
          'mdi-checkbox-marked': itemIsSelected,
        }"
        @click="toggleSelectedItem(selectIdObj)" />
      <i
        class="mdi mdi-dots-horizontal collection-list-item-menu-icon"
        @click.stop="toggleShowMenu" />
      <!-- ITEM -->
      <div
        v-if="item.item && item.item.type === 'file'"
        class="collection-item-item-column"
        @mousedown.prevent
        @click.exact="$goToItem(item)"
        @click.ctrl.exact="openItemInBackground(item)"
        @click.middle.exact.prevent="openItemInBackground(item)">
        <i class="mdi mdi-paperclip mdi-rotate-45" />
      </div>
      <a
        v-if="item.item && item.item.type === 'webpage'"
        class="collection-item-item-column"
        :href="item.item.address"
        target="_blank">
        <i class="mdi mdi-open-in-new" />
      </a>
      <div
        v-if="!item.item && item.type === 'card'"
        class="collection-item-item-column column-no-item" />
      <div
        v-if="item.type === 'note'"
        class="collection-item-item-column">
        <i class="mdi mdi-text" />
      </div>
      <!-- TITLE -->
      <div
        class="collection-item-title-column"
        @mousedown.prevent
        @click.exact="goToItem(item)"
        @click.ctrl.exact="addOpenItem(item)"
        @click.middle.exact.prevent="addOpenItem(item)">
        <span
          :contenteditable="editTitle"
          spellcheck="false"
          ref="titleText"
          class="title-text"
          @focus="editTitle = true"
          @blur="updateTitle"
          @keydown.enter.prevent="(e) => e.target.blur()"
          @paste="$clearFormattingOnPaste">
          {{ item.title }}
        </span>
        <span v-if="searchIsActive  && searchTerms.length" style="font-size: 0.7rem">
          <em>(in '{{ currentItemCollection.name }}')</em>
        </span>
      </div>
      <!-- ACTIONS -->
      <div class="item-actions">
        <div
          v-if="item.item"
          class="go-to-item"
          @mousedown.prevent
          @click.exact="item.item.type === 'webpage' ? $navigateTo({ externalUrl: item.item.address }) : $goToItem(item)"
          @click.ctrl.exact="item.item.type === 'webpage' ? $navigateTo({ externalUrl: item.item.address }) : openItemInBackground(item)"
          @click.middle.exact.prevent="item.item.type === 'webpage' ? $navigateTo({ externalUrl: item.item.address }) : openItemInBackground(item)">
          Go to {{ item.item.type }}
        </div>
        <div
          v-else-if="item.type === 'card'"
          class="go-to-item no-item">
          No item
        </div>
      </div>
      <i
        :class="pendingStarsIcon({ itemId: item.id }) || starsIcon(item)" class="collection-list-star-icon"
        @click.stop="processStars(item)"
        @mouseleave="updateStars" />
      <div class="collection-item-date-modified-column">
        {{ $formatDateConcise(item.dateLastModified) }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';

export default {
  name: 'ItemRow',
  props: {
    item: Object,
    batchSelectActive: Boolean,
    itemIsSelected: Boolean,
    selectCount: Number,
    selectIdObj: Object,
    contextMenu: Object,
    sections: Array,
  },
  data() {
    return {
      pendingStars: {
        id: null,
        stars: 0,
      },
      itemTitle: null,
      editTitle: false,
      contextMenuShowSections: false,
      newSection: null,
    };
  },
  computed: {
    ...mapState({
      currentItemCollection: function(state) { // eslint-disable-line
        // The selectedCollection fallback is in case a card is added to a new collection that does not yet have an id, due to which currentCard still lacks a relatedCollectionId
        const fallback = { id: null, name: '-' };
        if (!this.item) return fallback;

        const relatedCollectionId = this.item.relatedCollectionId || '';
        return state.collections.collectionNodes[relatedCollectionId] || state.collections.archivedCollectionNodes[relatedCollectionId] || fallback;
      },
      selectedCollection: state => state.collections.selectedCollection,
      searchTerms: state => state.search.terms,
      searchIsActive: state => state.appState.search.isActive,
      storedSidebarWidth: state => state.appState.localSettings.sidebarWidth,
    }),
    menuStyleObject() {
      const { displayFor, top, bottom, left, right } = this.contextMenu;
      // const noCoords = !top && !bottom && !left && !right;
      const noXCoords = !left && !right;
      const noYCoords = !top && !bottom;
      // Not displayed for this item, no style needed
      if (displayFor.id !== this.item.id || displayFor.type !== this.item.type) return {};
      // Suppose this is a card, and a note with the same id is displayed. Then we get false || true. Eval to true.
      // Suppose this is a card, and this card is displayed. Then we get false || false. Eval to false.
      // Suppose this is a card, and a card w/ another id is displayed. Then we get true || false. Eval to true.

      // Opened menu via dots, only need to specify default left & top value
      if (noXCoords && noYCoords) return { left: '3.75rem', top: '1rem' };
      // Opened menu via dots but Y screen overflow, so set default left value & adapted bottom value (instead of top value)
      if (noXCoords && bottom) return { left: '3.75rem', bottom: '1rem' };

      // Open via right-click, determine coords
      const styleObj = {};
      if (top) styleObj.top = `${top}px`;
      if (bottom) styleObj.bottom = `${bottom}px`;
      if (left) styleObj.left = `${left}px`;
      if (right) styleObj.right = `${right}px`;
      return styleObj;
    },
    subMenuStyleObject() {
      if (this.contextMenu.displayFor.id !== this.item.id && this.contextMenu.displayFor.type !== this.item.type) return {};
      // If the context menu overflows horizontally, the submenu should be placed on the left of the context menu
      if (this.contextMenu.right) return { right: '10rem' };
      return { left: '10rem' };
    },
  },
  methods: {
    ...mapActions({
      openInWebViewer: 'files/openInWebViewer',
      addNavigationToHistory: 'appState/addNavigationToHistory',
      toggleSelectedItem: 'appState/toggleSelectedItem',
      showAddedItemInBackgroundIndicator: 'appState/showAddedItemInBackgroundIndicator',
      updateCollectionNotes: 'notes/updateCollectionNotes',
      deleteCollectionNotes: 'notes/deleteCollectionNotes',
    }),
    ...mapMutations({
      AddOpenItem: 'appState/AddOpenItem',
    }),
    addSection(event) {
      if (!this.newSection) return; // Tabbing away results in double calling this: for Tab and for blur. Prevent updating with null for second call.

      if (this.itemIsSelected) this.$emit('update-section', this.newSection);
      else if (this.item.type === 'card') {
        this.$updateCard({
          id: this.item.id,
          data: {
            section: this.newSection,
          },
        });
      }
      else if (this.item.type === 'note') {
        this.updateCollectionNotes({
          noteId: this.item.id,
          collectionId: this.item.relatedCollectionId,
          section: this.newSection,
          dateLastModified: this.$getCurrentUTC(),
        });
      }

      this.newSection = null;
      event.target.blur();
      this.$emit('hide-menu');
    },
    updateSection(section) {
      if (this.item.type === 'card') {
        this.$updateCard({
          id: this.item.id,
          data: {
            section,
          },
        });
      }
      else if (this.item.type === 'note') {
        this.updateCollectionNotes({
          noteId: this.item.id,
          collectionId: this.item.relatedCollectionId,
          section,
          dateLastModified: this.$getCurrentUTC(),
        });
      }

      // For some reason this is necessary, though an hide-menu event is also emitted by the click on the context menu... very weird.
      this.$emit('hide-menu');
    },
    toggleShowMenu(e, isRightClick) {
      const isMenuDotsClick = e.target.className.includes('collection-list-item-menu-icon');
      // Clicking on menu dots while the dots menu is active hides menu
      if (isMenuDotsClick && this.contextMenu.displayFor.type === this.item.type && this.contextMenu.displayFor.id === this.item.id && this.menuStyleObject.left === '3.75rem') {
        this.$emit('hide-menu');
        return;
      }

      // All other click events show the menu
      this.contextMenuShowSections = false;
      const coords = this.$refs['row-element'].getBoundingClientRect();
      const coordsObj = {
        top: coords.top,
        bottom: coords.bottom,
        left: coords.left,
        right: coords.right,
      };
      this.$emit('show-context-menu', e, coordsObj, { type: this.item.type, id: this.item.id });
    },
    goToItem(item) {
      if (this.editTitle) return;

      if (item.type === 'card') this.$goToCard(item);
      else if (item.type === 'note') this.$goToNote(item);
    },
    addOpenItem(item) {
      this.AddOpenItem(item);
    },
    updateTitle(e) {
      const newTitle = e.target.innerText.trim();

      if (this.item.type === 'card') {
        this.$updateCard({
          id: this.item.id,
          data: {
            title: newTitle,
          },
        });
      }
      else if (this.item.type === 'note') {
        const now = this.$getCurrentUTC();
        this.updateCollectionNotes({
          noteId: this.item.id,
          collectionId: this.item.relatedCollectionId,
          title: newTitle,
          dateLastModified: now,
        });
      }

      this.editTitle = false;

      if (window.getSelection) window.getSelection().removeAllRanges();
      else if (document.selection) document.selection.empty();
    },
    async editItemTitle() {
      if (this.editTitle) {
        this.$refs.titleText.blur();
        return;
      }

      this.editTitle = true;
      await this.$nextTick();
      this.$refs.titleText.focus();

      const node = this.$refs.titleText;
      if (document.body.createTextRange) {
        const range = document.body.createTextRange();
        range.moveToElementText(node);
        range.select();
      }
      else if (window.getSelection) {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(node);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      else {
        console.warn('Could not select text in node: Unsupported browser.');
      }

      this.$emit('hide-menu');
    },
    openItemInBackground(card) {
      this.openInWebViewer(card);
      this.addNavigationToHistory(`/item/${card.id}`);
      this.showAddedItemInBackgroundIndicator();
    },
    starsIcon(item) {
      if (item.stars === 1) return 'mdi mdi-star-outline mdi-18px';
      if (item.stars === 2) return 'mdi mdi-star-half mdi-18px';
      if (item.stars === 3) return 'mdi mdi-star mdi-18px';
      return 'mdi mdi-star-outline mdi-18px inactive';
    },
    pendingStarsIcon({ itemId }) {
      const { id } = this.pendingStars;
      if (!id || itemId !== id) return null;

      return this.starsIcon(this.pendingStars);
    },
    processStars(item) {
      if (this.contextMenu.displayFor.type === this.item.type && this.contextMenu.displayFor.id === this.item.id) this.$emit('hide-menu');

      const { id } = item;
      let { stars } = item;
      if (this.pendingStars.id === id) ({ stars } = this.pendingStars);
      stars += 1;
      if (stars > 3) stars = 0;

      this.pendingStars.id = id;
      this.pendingStars.stars = stars;
    },
    updateStars() {
      if (!this.pendingStars.id) return;

      const dateLastModified = this.$getCurrentUTC();
      const { id, stars } = this.pendingStars;

      if (this.item.type === 'card') {
        this.$updateCard({
          id,
          data: {
            stars,
            dateLastModified,
          },
        });
      }
      else if (this.item.type === 'note') {
        this.updateCollectionNotes({
          collectionId: this.item.relatedCollectionId,
          noteId: this.item.id,
          stars,
          dateLastModified,
        });
      }

      this.pendingStars.id = null;
      this.pendingStars.stars = 0;
    },
    deleteItem() {
      if (this.itemIsSelected) {
        this.$emit('delete-selected');
        return;
      }

      this.$buefy.dialog.confirm({
        message: `About to delete <em>${this.item.title}</em>. Are you sure?<br><br>
                  <em style="font-size: 0.85rem;">Note that this cannot be undone.</em><br>`,
        cancelText: 'Cancel',
        confirmText: 'Yes',
        type: 'is-danger',
        hasIcon: false,
        onConfirm: () => {
          if (this.item.type === 'card') this.$deleteCard(this.item.id);
          else if (this.item.type === 'note') this.deleteCollectionNotes({ noteId: this.item.id, collectionId: this.item.relatedCollectionId });

          this.toggleSelectedItem({ id: this.selectId, itemId: this.item.id, itemType: this.item.type, forceRemove: true });
          this.$goToCollection({ id: this.selectedCollection });
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/colors';
@import '~styles/fonts';

// ROW
.collection-item {
  width: 100%;
  max-height: calc(1.75rem + 2px);
  position: relative;
}
.collection-item-row {
  position: relative;
  width: 100%;
  min-height: 1.75rem;
  height: 1.75rem;
  display: flex;
  align-items: center;
  padding: 0 1.5rem 0 0.75rem;
  z-index: 2;
  user-select: none;
}
.collection-item-row:hover,
.collection-item-row-selected {
  background-color: #eeeeee;
}
.collection-item-row-active {
  background: $primary;
  user-select: text;
  cursor: default !important;
}
.collection-item-row-active .collection-list-select-icon {
  opacity: 0.75;
}

// ICONS IN ROW (RIGHT)
.collection-item-item-column {
  width: 2.5rem;
  text-align: center;
  color: $black;
  border-radius: 1rem;
  cursor: pointer;
}
.collection-item-item-column:hover {
  background: $primary-dark;
}
.column-no-item:hover {
  background-color: $primary-light;
}
.collection-list-star-icon {
  width: 1.5rem;
  padding: 0 0.25rem;
  opacity: 0.6;
  cursor: pointer;
}
.collection-list-select-icon {
  opacity: 0;
  margin-right: 0.2rem;
  cursor: pointer;
  font-size: 1.15rem;
  width: 1.5rem;
}
.collection-item-row:hover .collection-list-select-icon,
.collection-item-row:hover .collection-list-item-menu-icon {
  opacity: 0.75;
}
.collection-item-row:hover .collection-list-star-icon:hover,
.collection-item-row:hover .collection-list-item-menu-icon:hover {
  opacity: 1;
}
.display-select-checkboxes {
  opacity: 0.75;
}
.collection-list-item-menu-icon {
  font-size: 1.15rem;
  opacity: 0.5;
  cursor: pointer;
}

// CONTEXT MENU
.collection-list-item-menu,
.collection-list-item-menu-sub {
  position: absolute;
  width: 10rem;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: stretch;
  background-color: #eeeeee !important;
  box-shadow: 1px 1px 3px 1px $primary-dark;
  border-radius: 0 0.25rem 0.25rem 0.25rem;
  font-size: 0.8rem;
  opacity: 1;
  padding: 0 !important;
  white-space: normal;
  z-index: 5;
}
.collection-list-item-menu-sub {
  top: -2rem;
  max-height: 135px;
  overflow-y: auto;
}
.collection-list-item-menu-header {
  cursor: default !important;
  background-color: #eeeeee !important;
}
.collection-list-item-menu .disabled {
  color: $primary-dark;
  font-style: italic;
  cursor: default;
  background-color: #eeeeee !important;
}
.collection-list-item-menu-divider {
  border-bottom: 1px solid $primary;
  margin: 0.25rem 0;
}
.collection-list-item-menu-item {
  position: relative;
  cursor: pointer;
  user-select: none;
  width: 100%;
  padding: 0.25rem 0.5rem !important;
  font-family: $headerFont;
}
.collection-list-item-menu-item:first-child {
  margin-top: 0.25rem;
}
.collection-list-item-menu-item:last-child {
  margin-bottom: 0.25rem;
}
.collection-list-item-menu-item:hover {
  background-color: $primary-light;
}
.collection-list-item-menu-item:hover > .new-section-input {
  background-color: $primary-light !important;
}
.collection-list-item-menu-item .new-section-input {
  border: none;
  outline: none;
  width: 100%;
  height: 100%;
  font-family: $headerFont;
  background-color: #eeeeee !important;
}

// STARS ICON
.inactive {
  color: rgba($primary-dark, 0.5);
}

// TITLE
.collection-item-title-column {
  display: inline-block;
  width: calc(100% - 13.5rem); // All width minus icons and right hand side
  padding: 0 0.25rem;
  cursor: pointer;
  color: #4a4a4a; // if there's a webpage linked, this is an '<a>' tag, prevent it from becoming blue/purple/whatever
  font-size: 0.9rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  cursor: pointer;
}
.collection-item-title-column i {
  margin-right: 0.25rem;
  opacity: 0.6;
  transition: opacity 0.3s;
}
.title-text {
  outline: none;
}
.collection-item-row:hover .collection-item-title-column i {
  opacity: 1;
}
.collection-item-row:hover .collection-item-title-column,
.collection-item-row-selected .collection-item-title-column {
  width: calc(100% - 22.5rem);
}

// DATE MODIFIED
.collection-item-date-modified-column {
  display: inline-block;
  width: 7rem;
  font-size: 0.7rem;
  line-height: 0.7rem;
  color: $primary-dark;
  text-align: right;
  white-space: nowrap;
}

// GO TO ITEM
.item-actions {
  display: inline-block;
  height: 100%;
  width: 4.5rem;
  width: 0;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.3s 0.1s;
  transition: opacity 0s;
  visibility: hidden;
  padding-right: 1rem;
}
.go-to-item {
  line-height: 1.7rem;
  border-radius: 0.25rem 0 0;
  background-color: $secondary;
  width: 100%;
  height: 100%;
  text-align: center;
  font-size: 0.85rem;
  color: $primary-light;
  // font-weight: 600;
  font-family: $headerFont;
  cursor: pointer;
  border-bottom: 1px solid #eeeeee;
  border-top: 1px solid #eeeeee;
  opacity: 0.6;
  transition: opacity 0.1s;
}
.go-to-item:hover {
  opacity: 1;
}
.go-to-item.no-item {
  cursor: default;
}
.go-to-item.no-item:hover {
  color: $primary;
}
.collection-item-row:hover .item-actions,
.collection-item-row-selected .item-actions {
  width: 10rem;
  visibility: visible;
  opacity: 1;
}

// FADE FOR CONTEXT MENU
.fade-leave-active {
  transition: opacity 0.15s;
}
.fade-enter-active {
  transition: opacity 0.15s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
